import moment from "moment-jalaali";

export const isPersianCalendar = (language: string): boolean =>
  language?.split("-")[0] === "fa";

export const getCurrentCalendarYear = (language: string): number => {
  if (isPersianCalendar(language)) {
    return moment().jYear();
  }
  return moment().year();
};

/** Start of Farvardin 1 (Jalali) or Jan 1 (Gregorian), as UTC ISO string */
export const getJalaliYearStartISO = (year: number): string =>
  moment(`${year}-01-01`, "jYYYY-jMM-jDD")
    .startOf("day")
    .utc()
    .toISOString();

/** End of Esfand 29/30 (Jalali), as UTC ISO string */
export const getJalaliYearEndISO = (year: number): string => {
  const day = moment.jDaysInMonth(year, 11);
  return moment(`${year}-12-${day}`, "jYYYY-jMM-jDD")
    .endOf("day")
    .utc()
    .toISOString();
};

/** Start of Jan 1 (Gregorian), as UTC ISO string */
export const getGregorianYearStartISO = (year: number): string =>
  new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0)).toISOString();

/** End of Dec 31 (Gregorian), as UTC ISO string */
export const getGregorianYearEndISO = (year: number): string =>
  new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999)).toISOString();

export const getYearStartISO = (year: number, language: string): string =>
  isPersianCalendar(language)
    ? getJalaliYearStartISO(year)
    : getGregorianYearStartISO(year);

export const getYearEndISO = (year: number, language: string): string =>
  isPersianCalendar(language)
    ? getJalaliYearEndISO(year)
    : getGregorianYearEndISO(year);

export const extractCalendarYearFromISO = (
  isoString: string,
  language: string
): number => {
  if (!isoString) {
    return getCurrentCalendarYear(language);
  }

  try {
    if (isPersianCalendar(language)) {
      const persianDate = moment(isoString);
      if (persianDate.isValid()) {
        return persianDate.jYear();
      }
    }

    const date = new Date(isoString);
    if (!Number.isNaN(date.getTime())) {
      return date.getUTCFullYear();
    }
  } catch {
    // fall through to current year
  }

  return getCurrentCalendarYear(language);
};

export const getDefaultYearRange = (
  language: string
): { fromDate: string; toDate: string } => {
  const year = getCurrentCalendarYear(language);
  return {
    fromDate: getYearStartISO(year, language),
    toDate: getYearEndISO(year, language),
  };
};

export const getYearOptions = (
  language: string,
  pastYears = 5
): number[] => {
  const currentYear = getCurrentCalendarYear(language);
  const years: number[] = [];
  for (let year = currentYear - pastYears; year <= currentYear; year++) {
    years.push(year);
  }
  return years;
};
